import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  FONT_OPTIONS,
  FONT_CATEGORY_LABEL,
  type FontOption,
} from "../../lib/fonts";
import { useDismissable } from "../../hooks/useDismissable";

// Selector de fuente para un slot semántico. Grid de tarjetas (NO
// <select> nativo) porque cada opción se renderiza en SU PROPIA
// tipografía — el styling de <option> no es confiable entre browsers
// (Chrome/Windows ignora font-family por option, Firefox lo respeta,
// etc.). Las tarjetas dan control total.
//
// onChange solo cambia el state local; aplicar las fonts a toda la
// página solo pasa cuando el form se guarda. Antes aplicaba en vivo
// y eso transformaba el dropdown completo, lo que confundía al user.
//
// Reorg UX 2026-06-25: el selector pasó de un grid siempre-visible de
// ~15 fuentes (×3 slots = página interminable) a este popover compacto.
// El botón muestra la fuente elegida en su propia tipografía; el panel
// con el grid visual se abre on-demand. NO es el patrón "colapsar toda
// la card con Editar" que el dueño rechazó: es un dropdown por campo.
export function FontPicker({
  label,
  slot,
  value,
  onChange,
}: {
  label: string;
  slot: "heading" | "body" | "display";
  value: string;
  onChange: (family: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // Cerrar al clickear afuera o con Escape. Sin esto, saltar de un slot a
  // otro deja varios paneles abiertos pisándose.
  const ref = useDismissable<HTMLDivElement>(open, () => setOpen(false));

  // Agrupar por categoría para el panel.
  const grouped = FONT_OPTIONS.reduce<Record<string, FontOption[]>>(
    (acc, opt) => {
      (acc[opt.category] ??= []).push(opt);
      return acc;
    },
    {},
  );

  const current = FONT_OPTIONS.find((o) => o.family === value);
  // Sample del preview: específico de la fuente si lo define (ej. Bebas
  // Neue solo mayúsculas → "GIRAR PREMIO"), sino representativo del slot.
  const sample =
    current?.sample ??
    (slot === "display"
      ? "GIRAR PREMIO"
      : slot === "heading"
      ? "¡Felicitaciones!"
      : "Texto de ejemplo");

  return (
    <div className="flex flex-col gap-1.5 text-sm" ref={ref}>
      <span className="opacity-80">{label}</span>

      <div className="relative">
        {/* Trigger compacto: muestra el nombre + el sample en la fuente
            elegida. Click abre/cierra el grid. */}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-3 text-left transition hover:border-white/30"
        >
          <span className="min-w-0 flex-1">
            <span className="block font-sans text-[10px] uppercase tracking-wide opacity-50">
              {value}
            </span>
            <span
              className="block truncate text-xl font-bold leading-tight"
              style={{ fontFamily: `"${value}", "Nunito", sans-serif` }}
            >
              {sample}
            </span>
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 opacity-60 transition ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Panel: grid de tarjetas agrupadas por categoría. Cada tarjeta
            se renderiza en SU propia tipografía para previsualizar. Click
            elige y cierra. Absolute para no empujar el resto del form. */}
        {open && (
          <div
            role="listbox"
            className="absolute left-0 right-0 top-full z-30 mt-2 max-h-80 overflow-y-auto rounded-lg border border-white/15 bg-[#1a1540] p-3 shadow-2xl"
          >
            {Object.entries(grouped).map(([cat, opts]) => (
              <div key={cat} className="mb-3 flex flex-col gap-1.5 last:mb-0">
                <div className="font-sans text-[10px] uppercase tracking-wide opacity-50">
                  {FONT_CATEGORY_LABEL[cat as keyof typeof FONT_CATEGORY_LABEL]}
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                  {opts.map((o) => {
                    const selected = o.family === value;
                    return (
                      <button
                        key={o.family}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          onChange(o.family);
                          setOpen(false);
                        }}
                        className={`rounded-lg border-2 px-3 py-2 text-left transition ${
                          selected
                            ? "border-[#a78bfa] bg-[#a78bfa]/10"
                            : "border-white/10 bg-white/5 hover:border-white/30"
                        }`}
                        style={{
                          fontFamily: `"${o.family}", "Nunito", sans-serif`,
                        }}
                      >
                        <div className="text-base font-bold leading-tight">
                          {o.label}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
